import { useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Handshake, ArrowRight, ArrowUpRight, Crown, BadgeCheck, Heart } from "lucide-react";
import { useSponsors, trackClick, trackImpression } from "@/lib/useSponsors";

const TIER_RIBBON = {
  principal: { label: "Principal", cls: "bg-gold text-navy" },
  oro: { label: "Oro", cls: "bg-gold/90 text-navy" },
  plata: { label: "Plata", cls: "bg-gray-200 text-navy" },
  bronce: { label: "Aliado", cls: "bg-[#cd7f32] text-white" },
};

function useSeen(id, placement) {
  const seen = useRef(false);
  useEffect(() => {
    if (id && id !== "none" && !seen.current) { seen.current = true; trackImpression(id, placement); }
  }, [id, placement]);
}

function Clickable({ s, className, children, testid }) {
  return (
    <Link to={`/patrocinadores/${s.slug}`} onClick={() => trackClick(s.id)}
       aria-label={s.name} data-testid={testid} className={className}>{children}</Link>
  );
}

/* Tile de marca sobre fondo navy (representación grande del logo). */
function BrandTile({ s }) {
  useSeen(s.id, "wall");
  const ribbon = TIER_RIBBON[s.tier] || TIER_RIBBON.bronce;
  return (
    <Clickable s={s} testid={`sponsor-wall-${s.slug || s.id}`}
      className="group relative block rounded-2xl bg-white p-5 shadow-[0_10px_30px_rgba(0,0,0,0.18)] transition-all duration-300 hover:-translate-y-1.5">
      <span className={`absolute -top-2.5 left-4 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow ${ribbon.cls}`}>{ribbon.label}</span>
      <div className="h-24 flex items-center justify-center">
        {s.logo_url
          ? <img src={s.logo_url} alt={s.name} loading="lazy" className="max-h-24 w-auto object-contain" />
          : <span className="text-xl font-black text-navy text-center" style={{ fontFamily: "Outfit" }}>{s.name}</span>}
      </div>
      <div className="mt-3 text-center">
        <div className="font-extrabold text-navy leading-tight" style={{ fontFamily: "Outfit" }}>{s.name}</div>
        {s.category && <div className="text-[11px] font-bold uppercase tracking-wide text-gold">{s.category}</div>}
      </div>
      {s.url && <ArrowUpRight className="absolute top-4 right-4 h-4 w-4 text-navy/20 group-hover:text-gold transition-colors" />}
    </Clickable>
  );
}

/* ---------- BLOQUE INMERSIVO DE SOCIOS (home) ---------- */
export function SponsorWall() {
  const sponsors = useSponsors("wall");
  if (!sponsors.length) return null;
  const principal = sponsors.find((s) => s.tier === "principal");
  const rest = sponsors.filter((s) => s !== principal);

  return (
    <section className="relative bg-navy text-white overflow-hidden" data-testid="sponsor-wall">
      {/* glows de marca */}
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-gold/15 blur-[100px]" />
      <div className="absolute -bottom-32 -right-16 h-96 w-96 rounded-full bg-[#c0392b]/15 blur-[100px]" />
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-gold">
            <Heart className="h-4 w-4 fill-gold" /> Nuestros socios comerciales
          </span>
          <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight text-white" style={{ fontFamily: "Outfit" }}>
            Ellos hacen posible el club
          </h2>
          <p className="mt-4 text-white/70 text-base sm:text-lg">
            Cada logo es una empresa que elige apostar por nuestra comunidad. Gracias por bancar cada partido, cada pibe y cada sueño.
          </p>
        </div>

        {/* Spotlight del Principal */}
        {principal && (
          <div className="mt-14 rounded-3xl border border-gold/30 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 lg:grid lg:grid-cols-12 lg:gap-8 lg:items-center">
            <div className="lg:col-span-5">
              <Clickable s={principal} testid={`sponsor-wall-${principal.slug || principal.id}`}
                className="group block rounded-2xl bg-white p-8 shadow-2xl transition-transform duration-300 hover:-translate-y-1">
                <div className="h-28 sm:h-32 flex items-center justify-center">
                  {principal.logo_url
                    ? <img src={principal.logo_url} alt={principal.name} className="max-h-32 w-auto object-contain" />
                    : <span className="text-3xl font-black text-navy" style={{ fontFamily: "Outfit" }}>{principal.name}</span>}
                </div>
              </Clickable>
            </div>
            <div className="mt-6 lg:mt-0 lg:col-span-7">
              <span className="inline-flex items-center gap-1.5 bg-gold text-navy text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full">
                <Crown className="h-3.5 w-3.5" /> Sponsor Principal
              </span>
              <h3 className="mt-4 text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: "Outfit" }}>{principal.name}</h3>
              {principal.category && <div className="text-sm font-bold uppercase tracking-wide text-gold">{principal.category}</div>}
              {principal.description && <p className="mt-3 text-white/75 max-w-xl">{principal.description}</p>}
              {principal.url && (
                <a href={principal.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackClick(principal.id)}
                   className="mt-5 inline-flex items-center gap-2 bg-gold text-navy font-bold px-6 py-3 rounded-xl hover:-translate-y-0.5 transition-transform">
                  Visitar sitio <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Grilla de socios */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6">
          {rest.map((s) => <BrandTile key={s.id} s={s} />)}
        </div>

        {/* CTA venta */}
        <div className="mt-14 text-center">
          <p className="text-white/60 text-sm">¿Tu marca también quiere ser parte de la historia del club?</p>
          <Link to="/patrocinadores" data-testid="sponsor-wall-cta"
            className="mt-4 inline-flex items-center gap-2 bg-gold text-navy font-bold px-8 py-4 rounded-xl hover:-translate-y-0.5 transition-transform shadow-lg">
            Quiero auspiciar <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- Spotlight contextual por categoría (distribuido en el home) ---------- */
export function SponsorSpotlight({ preferCategory, eyebrow = "En alianza con" }) {
  const sponsors = useSponsors("wall");
  const s = useMemo(() => {
    if (!sponsors.length) return null;
    const byCat = preferCategory && sponsors.find((x) => (x.category || "").toLowerCase().includes(preferCategory.toLowerCase()));
    return byCat || sponsors.find((x) => x.tier !== "principal") || sponsors[0];
  }, [sponsors, preferCategory]);
  useSeen(s?.id || "none", "home_spotlight");
  if (!s) return null;

  return (
    <section className="bg-[#eef1f7] py-8" data-testid="sponsor-spotlight">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Clickable s={s} testid={`sponsor-spotlight-${s.slug || s.id}`}
          className="group relative flex items-center gap-5 rounded-2xl bg-white border border-navy/10 shadow-sm pl-6 pr-5 py-5 overflow-hidden transition-all duration-300 hover:shadow-lg">
          <span className="absolute left-0 inset-y-0 w-1.5 bg-gold" />
          <div className="shrink-0 h-16 w-28 flex items-center justify-center">
            {s.logo_url
              ? <img src={s.logo_url} alt={s.name} loading="lazy" className="max-h-16 w-auto object-contain" />
              : <Handshake className="h-8 w-8 text-navy/40" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gold">
              <BadgeCheck className="h-3.5 w-3.5" /> {eyebrow}{s.category ? ` · ${s.category}` : ""}
            </div>
            <div className="text-lg font-extrabold text-navy leading-tight" style={{ fontFamily: "Outfit" }}>{s.name}</div>
            {s.description && <div className="text-sm text-gray-500 line-clamp-1">{s.description}</div>}
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold text-navy group-hover:text-gold transition-colors">
            Ver más <ArrowRight className="h-4 w-4" />
          </span>
        </Clickable>
      </div>
    </section>
  );
}

/* ---------- "Temporada presentada por" (barra bajo el hero) ---------- */
export function SponsorHeroBadge() {
  const sponsors = useSponsors("hero");
  useSeen(sponsors[0]?.id || "none", "hero");
  if (!sponsors.length) return null;
  const s = sponsors[0];
  const inner = (
    <div className="flex items-center justify-center gap-4 flex-wrap">
      <span className="text-xs font-black uppercase tracking-[0.22em] text-gold">Temporada 2026 presentada por</span>
      {s.logo_url
        ? <img src={s.logo_url} alt={s.name} className="h-9 sm:h-11 w-auto object-contain brightness-0 invert" />
        : <span className="text-lg font-extrabold text-white" style={{ fontFamily: "Outfit" }}>{s.name}</span>}
    </div>
  );
  return (
    <div className="bg-navy border-y border-gold/25" data-testid="sponsor-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <Link to={`/patrocinadores/${s.slug}`} onClick={() => trackClick(s.id)} aria-label={s.name} className="block">{inner}</Link>
      </div>
    </div>
  );
}

/* ---------- Tira de logos en el footer (mobile-first) ---------- */
export function SponsorStrip() {
  const sponsors = useSponsors("footer");
  useSeen(sponsors[0]?.id || "none", "footer");
  if (!sponsors.length) return null;

  // Repetimos hasta tener suficientes logos para llenar la fila
  const reps = Math.max(1, Math.ceil(8 / sponsors.length));
  const group = Array.from({ length: reps }).flatMap(() => sponsors);
  // Velocidad constante: ~4.5s por logo. Valor de tiempo directo (compatible con Safari).
  const duration = group.length * 4.5;

  const renderGroup = (hidden) => (
    <div className="logo-marquee__group" aria-hidden={hidden}>
      {group.map((s, i) => (
        <Link
          key={`${hidden ? "b" : "a"}-${i}`}
          to={`/patrocinadores/${s.slug}`}
          onClick={() => trackClick(s.id)}
          aria-label={s.name}
          tabIndex={hidden ? -1 : 0}
          className="mx-6 sm:mx-10 flex items-center justify-center"
        >
          {s.logo_url
            ? <img src={s.logo_url} alt={s.name} className="h-10 sm:h-14 w-auto max-w-[8rem] object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
            : <span className="text-navy/70 font-bold whitespace-nowrap">{s.name}</span>}
        </Link>
      ))}
    </div>
  );

  return (
    <div className="bg-white border-t border-navy/10 py-8" data-testid="sponsor-strip">
      <p className="text-center text-xs font-black uppercase tracking-[0.22em] text-navy/50">Marcas que hacen posible el club</p>
      <div className="logo-marquee mt-6">
        <div className="logo-marquee__track" style={{ animationDuration: `${duration}s` }}>
          {renderGroup(false)}
          {renderGroup(true)}
        </div>
      </div>
    </div>
  );
}

/* ---------- Banner contextual (secciones internas) ---------- */
export function SponsorBanner({ placement }) {
  const sponsors = useSponsors(placement);
  useSeen(sponsors[0]?.id || "none", placement);
  if (!sponsors.length) return null;
  const s = sponsors[0];
  return (
    <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 py-8" data-testid={`sponsor-banner-${placement}`}>
      <div className="relative overflow-hidden rounded-2xl bg-navy text-white border border-gold/25 px-6 sm:px-8 py-6 flex items-center gap-5 sm:gap-8">
        <div className="absolute -top-16 -right-12 h-48 w-48 rounded-full bg-gold/15 blur-3xl" />
        <span className="absolute top-3 left-6 text-[10px] font-black uppercase tracking-[0.18em] text-gold/70">Espacio patrocinado</span>
        <Link to={`/patrocinadores/${s.slug}`} onClick={() => trackClick(s.id)} aria-label={`Ver perfil de ${s.name}`}
          className="relative shrink-0 bg-white rounded-xl p-3 flex items-center justify-center h-20 w-32 sm:w-40 transition-transform hover:-translate-y-0.5">
          {s.logo_url ? <img src={s.logo_url} alt={s.name} className="max-h-14 w-auto object-contain" /> : <Handshake className="h-8 w-8 text-navy/40" />}
        </Link>
        <div className="relative min-w-0 flex-1 pt-2">
          {s.category && <div className="text-[11px] font-black uppercase tracking-wide text-gold">{s.category}</div>}
          <div className="text-lg sm:text-xl font-extrabold truncate" style={{ fontFamily: "Outfit" }}>{s.name}</div>
          {s.description && <p className="text-sm text-white/70 line-clamp-1">{s.description}</p>}
        </div>
        {s.url && (
          <a href={s.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackClick(s.id)}
             data-testid={`sponsor-banner-cta-${placement}`}
             className="relative shrink-0 inline-flex items-center gap-1.5 bg-gold text-navy font-bold text-sm px-5 py-2.5 rounded-lg hover:-translate-y-0.5 transition-transform">
            Visitar <ArrowRight className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}
